// src/providers/AppThemeProvider.tsx
import React, { createContext, useContext, useLayoutEffect, useMemo, useState } from "react";
import { ThemeProvider as StyledThemeProvider } from "styled-components";
import i18n from "../i18n";

type Mode = "light" | "dark";
type Ctx = { mode: Mode; toggle: () => void };
const ThemeModeCtx = createContext<Ctx | null>(null);

// ----- helpers -----
function getInitialMode(): Mode {
  const saved = (localStorage.getItem("theme") as Mode | null) ?? null;
  if (saved) return saved;
  const prefersDark = window.matchMedia?.("(prefers-color-scheme: dark)").matches;
  return prefersDark ? "dark" : "light";
}

function getInitialLang(): "ar" | "en" {
  // Read persisted language (prefer explicit 'lang' key, fall back to 'i18nextLng')
  const saved = ((localStorage.getItem("lang") || localStorage.getItem("i18nextLng") || "") as string).slice(0, 2) as "ar" | "en";
  return saved === "en" ? "en" : "ar";
}

function dirFromLang(lng: string): "rtl" | "ltr" {
  return lng.startsWith("ar") ? "rtl" : "ltr";
}

export function AppThemeProvider({ children }: { children: React.ReactNode }) {
  const [mode, setMode] = useState<Mode>(() => getInitialMode());
  const [lang, setLang] = useState<"ar" | "en">(() => getInitialLang());
  const [dir, setDir] = useState<"rtl" | "ltr">(() => dirFromLang(getInitialLang()));

  // Apply html attrs BEFORE paint to avoid flicker
  useLayoutEffect(() => {
    const d = dirFromLang(lang);
    document.documentElement.setAttribute("dir", d);
    document.documentElement.setAttribute("lang", lang);
    setDir(d);
    // Ensure i18n starts in Arabic by default if nothing chosen
    if ((i18n.language || "").slice(0, 2) !== lang) {
      i18n.changeLanguage(lang).catch(() => {});
    }
  }, [lang]);

  // Keep in sync if i18n language changes elsewhere
  useLayoutEffect(() => {
    const handler = (lng: string) => {
      const short = (lng || "ar").slice(0, 2) as "ar" | "en";
      setLang(short);
    };
    i18n.on("languageChanged", handler);
    return () => i18n.off("languageChanged", handler);
  }, []);

  // Persist + reflect theme on <html data-theme="">
  useLayoutEffect(() => {
    localStorage.setItem("theme", mode);
    document.documentElement.setAttribute("data-theme", mode);
  }, [mode]);

  const toggle = () => setMode((m) => (m === "dark" ? "light" : "dark"));

  const theme = useMemo(() => ({ dir, mode }), [dir, mode]);
  const ctx = useMemo(() => ({ mode, toggle }), [mode]);

  return (
    <ThemeModeCtx.Provider value={ctx}>
      <StyledThemeProvider theme={theme}>{children}</StyledThemeProvider>
    </ThemeModeCtx.Provider>
  );
}

export function useThemeMode() {
  const ctx = useContext(ThemeModeCtx);
  if (!ctx) throw new Error("useThemeMode must be used within AppThemeProvider");
  return ctx;
}
